// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * The model, pinned — and the two dtype choices, both of which were measured
 * rather than assumed.
 *
 * Plain `.mjs` with a `.d.mts` sibling rather than TypeScript, because
 * `scripts/make-reference.mjs` runs under bare Node with no TypeScript loader
 * and MUST read the same constants the application does. A reference vector
 * generated against a different revision or dtype than the tool loads would
 * make the boot-time self-check compare two different models, and it would then
 * either pass wrongly or fail wrongly — which is worse than having no check.
 */

/** MobileCLIP-S0, converted to ONNX by Xenova. Public, ungated, no token. */
export const MODEL_ID = 'Xenova/mobileclip_s0';

/**
 * An immutable commit, not `main`.
 *
 * A repository owner can replace `main` at any time, and every number this tool
 * prints depends on the weights being the ones its reference vectors were
 * computed from.
 */
export const MODEL_REVISION = '757d59c9c6870a76a4b0306f05f5061bca15c39f';

/**
 * VISION: fp16. The 11.8 MB int8 export is refused, and this is the single most
 * important line in the tool.
 *
 * Measured this build over 8 photographs x 8 queries, against the fp32 export:
 *
 *   fp16  cosine to fp32  mean 0.99999  min 0.99996   top-1 ranking agrees 8/8
 *   int8  cosine to fp32  mean 0.25297  min 0.12997   top-1 ranking agrees 0/8
 *
 * The int8 file does not blur the ranking, it destroys it — not one query in
 * eight returns the same best frame. It also loads without error and produces
 * embeddings of the right shape in a plausible range, so nothing about the
 * failure is visible from the outside. MobileCLIP-S0's vision tower is a
 * FastViT convolutional hybrid with squeeze-and-excite blocks, and per-tensor
 * int8 handles that badly; the repository's own config.json quietly steers
 * around it by declaring transformers.js_config.dtype.vision_model = "fp32".
 *
 * 11 MB of extra download is the correct price for every result being the
 * right one.
 */
export const VISION_DTYPE = 'fp16';
export const VISION_BYTES = 22876479;

/**
 * ...but ONLY on WebGPU. The WebAssembly tier gets fp32 (45,543,630 B).
 *
 * The reason is SPEED, measured — not a defect in fp16.
 *
 * Timed in Node on the CPU backend, which is the closest analogue to the
 * WebAssembly backend available without a browser, median per image over eight
 * photographs: fp32 949 ms, fp16 1,156 ms, int8 814 ms. Half-precision has no
 * hardware to exploit off the GPU, so it is ~22% SLOWER than full precision
 * there while being no more accurate. On the tier that is already the slow one,
 * paying 22.7 MB of download to go faster is the right trade; on WebGPU, where
 * fp16 is native and halves the memory, it is not.
 *
 * int8 is not an option on either tier at any speed — see the measurement above.
 *
 * HONESTY NOTE. This tool's own browser testing could NOT confirm the
 * WebAssembly tier end to end: the preview browser used for verification never
 * completed ONNX session creation on that backend for EITHER dtype, on a
 * machine at load average 45. That is evidence about the test environment, not
 * about fp16 — an earlier version of this comment claimed otherwise and was
 * wrong. The WebGPU tier was verified in a real browser, self-check and all.
 *
 * fp16 and fp32 agree to 0.99999 and rank identically (8/8), so an index built
 * on one tier stays valid on the other — see COMPATIBLE_VISION_DTYPES.
 */
export const VISION_DTYPE_WASM = 'fp32';
export const VISION_BYTES_WASM = 45543630;

/**
 * Vision quantisations whose embeddings mean the same thing.
 *
 * A saved index records which one produced it. fp16 and fp32 are
 * interchangeable — measured mean cosine 0.99999 and identical top-1 ranking on
 * 8 of 8 queries — so an index built on a laptop with a GPU must not be refused
 * on a phone without one. int8 is deliberately absent: it agrees with fp32 on
 * ZERO of eight queries, and reading an int8 index as though it were this one
 * would silently return the wrong moments.
 */
export const COMPATIBLE_VISION_DTYPES = ['fp16', 'fp32'];

/**
 * TEXT: q8, and there is genuinely no alternative worth having.
 *
 * fp16 CANNOT BE LOADED AT ALL — ONNX Runtime throws during graph
 * initialisation ("Attempting to get index by a name which does not exist:
 * InsertedPrecisionFreeCast_... for node /transformer.1/pre_norm_mha.0/Mul/
 * SimplifiedLayerNormFusion"), so the 85 MB file is not an option on any
 * backend. That leaves q8 at 42.8 MB or fp32 at 169.8 MB.
 *
 * q8 measures 0.893 mean cosine against fp32 text, which sounds alarming and is
 * not the number that matters. Paired with the fp16 vision tower it returns the
 * same best frame on 7 of 8 queries, and the single disagreement is the one
 * query with no true match in the set — where every score is noise and the
 * order is meaningless anyway. Discrimination between unrelated queries is
 * fully intact (pairwise cosine: fp32 min 0.420 / mean 0.608, q8 min 0.479 /
 * mean 0.610). It is also what the official Xenova/webgpu-mobileclip demo
 * ships.
 */
export const TEXT_DTYPE = 'q8';
export const TEXT_BYTES = 42799238;

/** The CLIP tokenizer that comes with the repo. */
export const TOKENIZER_BYTES = 2224081;

/**
 * What the user is told BEFORE a byte is fetched, not after — and it differs by
 * tier, because the vision file does.
 */
export const TOTAL_BYTES = VISION_BYTES + TEXT_BYTES + TOKENIZER_BYTES;
export const TOTAL_BYTES_WASM = VISION_BYTES_WASM + TEXT_BYTES + TOKENIZER_BYTES;

/** Embedding width of both towers. Measured by execution — config.json does not carry it. */
export const EMBED_DIM = 512;

/**
 * The vision tower's input is 256x256, NOT the usual CLIP 224, and there is
 * NO mean/std normalisation — preprocessor_config.json sets do_normalize false
 * and rescales by 1/255 only. Recorded here because it is the sort of thing a
 * later reader will "fix" back to the standard CLIP constants.
 */
export const IMAGE_SIZE = 256;

/**
 * The tokenizer MUST be called with padding:'max_length'.
 *
 * Anything else — dynamic padding, or no padding options at all — throws
 * "Non-zero status code returned while running Add node ... Attempting to
 * broadcast an axis by a dimension other than 1. 7 by 77". The 77 is baked into
 * the graph: passing max_length:32 throws the same error, so it is not a free
 * parameter. Leaving max_length unset works, because tokenizer_config.json
 * declares model_max_length 77.
 */
export const TOKENIZER_OPTIONS = { padding: 'max_length', truncation: true };

export const MODEL_URL = 'https://huggingface.co/Xenova/mobileclip_s0';
export const MODEL_LICENSE = 'Apple ML Research MobileCLIP — see the model card';
